import type { TrackEventPayload, TrackEventResponse } from "./types";
import {
  TrackEventPayloadSchema,
  TrackEventResponseSchema,
} from "./validation";
import type { SDKState } from "@/state/sdk-state";
import { $ZodError } from "zod/v4/core";
import { Logger } from "@/utils/logger";

export class LoyaltyService {
  private state: SDKState;
  private logger: Logger;

  constructor(state: SDKState, logger: Logger) {
    this.state = state;
    this.logger = logger.extend(this.constructor.name);
  }

  async trackEvent(payload: TrackEventPayload): Promise<TrackEventResponse> {
    if (!this.state) {
      throw new Error(
        this.logger.prefixMessage("SDK not initialized. Call init() first."),
      );
    }

    if (!this.state.clientId) {
      throw new Error(
        this.logger.prefixMessage(
          "Client ID not found. Set data-client-id attribute on script tag or pass to init().",
        ),
      );
    }

    // Validate payload
    try {
      TrackEventPayloadSchema.parse(payload);
    } catch (error) {
      if (error instanceof $ZodError) {
        const errorMessages = error.issues
          .map((issue) => issue.message)
          .join(", ");
        throw new Error(
          this.logger.prefixMessage(`Invalid payload: ${errorMessages}`),
        );
      }
      throw error;
    }

    const apiEndpoint = `${this.state.options.apiBase}/public/external-actions/ingest`;
    const actionId =
      this.state.actionRegistry.getChannelActionId(payload.action) ??
      payload.action;

    this.logger.info("Event tracking context", { state: this.state, actionId });

    const requestBody = {
      channelActionId: actionId,
      externalExperienceId: payload.externalExperienceId ?? null,
      externalObjectId: payload.externalObjectId ?? null,
      timestamp: new Date().toISOString(),
      location: payload.location ?? null,
      metadata: {
        ...payload.metadata,
        ...(payload.description && { description: payload.description }),
        source: "fanfest_sdk",
        sdk_version: "1.0.0",
      },
    };

    try {
      this.logger.info("Sending loyalty event", {
        action: payload.action,
        description: payload.description,
        hasLocation: !!payload.location,
      });

      const headers = new Headers();
      headers.set("Content-Type", "application/json");
      headers.set("x-app-id", this.state.clientId);

      const authToken = this.state.authState.authToken;
      if (authToken) {
        headers.set("authorization", authToken);
        const authChannelType =
          this.state.authState.value.get()?.authChannelType;
        if (authChannelType) {
          headers.set("x-auth-channel", authChannelType);
        }
      }

      const response = await fetch(apiEndpoint, {
        method: "POST",
        headers,
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      const data = TrackEventResponseSchema.parse(result);

      if ("success" in data) {
        return {
          didReward: false,
          success: false,
          points: 0,
          message: data.error ?? "Unknown error occurred",
        };
      }

      this.logger.info("Loyalty event tracked successfully");

      return {
        ...data,
        success: true,
        notificationMessage: data.notificationMessage ?? undefined,
        notificationTitle: data.notificationTitle ?? undefined,
      };
    } catch (error) {
      if (error instanceof $ZodError) {
        this.logger.warn(
          "Invalid API response format",
          error,
          error.cause,
          error.issues,
          error.stack,
        );
      } else {
        this.logger.error("Failed to track loyalty event", { error });
      }

      return {
        didReward: false,
        success: false,
        points: 0,
        message:
          error instanceof Error ? error.message : "Unknown error occurred",
      };
    }
  }
}
